import {
  DIRECTION_LOCK_PX,
  SWIPE_THRESHOLD_PX,
} from "../config/dashboardCarousel";

const STATE = {
  idle: "idle",
  tracking: "tracking",
  committed: "committed",
  cancelled: "cancelled",
};

/**
 * Reference gesture engine for single-finger horizontal carousel swipes.
 *
 * Rejects multi-touch, pinch, and vertical-dominant movement.
 * Preserves vertical scroll and browser pinch-zoom via touch-action.
 *
 * @param {object} options
 * @param {(direction: "left" | "right") => void} options.onSwipe
 * @param {number} [options.thresholdPx]
 * @param {number} [options.directionLockPx]
 */
export function createCarouselSwipeHandlers({
  onSwipe,
  thresholdPx = SWIPE_THRESHOLD_PX,
  directionLockPx = DIRECTION_LOCK_PX,
}) {
  let state = STATE.idle;
  let touchId = null;
  let startX = 0;
  let startY = 0;
  let hadMultiTouch = false;

  function reset() {
    state = STATE.idle;
    touchId = null;
    hadMultiTouch = false;
  }

  function findTouch(touches, id) {
    for (let i = 0; i < touches.length; i++) {
      if (touches[i].identifier === id) {
        return touches[i];
      }
    }
    return null;
  }

  function onTouchStart(e) {
    if (state !== STATE.idle) {
      hadMultiTouch = true;
      state = STATE.cancelled;
      return;
    }

    if (e.touches.length !== 1) {
      return;
    }

    const touch = e.touches[0];
    touchId = touch.identifier;
    startX = touch.clientX;
    startY = touch.clientY;
    state = STATE.tracking;
  }

  function onTouchMove(e) {
    if (state === STATE.idle || state === STATE.cancelled) {
      return;
    }

    if (e.touches.length > 1) {
      hadMultiTouch = true;
      state = STATE.cancelled;
      return;
    }

    const touch = findTouch(e.touches, touchId);
    if (!touch) {
      state = STATE.cancelled;
      return;
    }

    const deltaX = touch.clientX - startX;
    const deltaY = touch.clientY - startY;
    const absX = Math.abs(deltaX);
    const absY = Math.abs(deltaY);
    const totalMovement = Math.hypot(deltaX, deltaY);

    if (totalMovement < directionLockPx) {
      return;
    }

    if (absY >= absX) {
      state = STATE.cancelled;
      return;
    }

    state = STATE.committed;
    e.preventDefault();
  }

  function onTouchEnd(e) {
    if (hadMultiTouch || state === STATE.cancelled) {
      reset();
      return;
    }

    if (e.changedTouches.length !== 1) {
      reset();
      return;
    }

    const touch = e.changedTouches[0];
    if (touch.identifier !== touchId) {
      reset();
      return;
    }

    if (state !== STATE.committed) {
      reset();
      return;
    }

    const deltaX = touch.clientX - startX;
    if (Math.abs(deltaX) >= thresholdPx) {
      onSwipe(deltaX < 0 ? "left" : "right");
    }

    reset();
  }

  function onTouchCancel() {
    reset();
  }

  return {
    onTouchStart,
    onTouchMove,
    onTouchEnd,
    onTouchCancel,
    touchActionStyle: { touchAction: "pan-y pinch-zoom" },
  };
}
